import { inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AuthService } from '../../../services/auth.service';


function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const newPassword = group.get('newPassword')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;
  return newPassword === confirmPassword ? null : { passwordsMismatch: true };
}

/** Sub-view of the login card. */
export type LoginView = 'login' | 'newPassword' | 'forgotRequest' | 'forgotConfirm';


export abstract class BaseLoginComponent {
  private readonly fb = inject(FormBuilder);
  protected readonly authService = inject(AuthService);

  abstract navigateAfterLogin():Promise<void> | void;

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });

  newPasswordForm = this.fb.group(
    {
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    },
    { validators: passwordsMatchValidator },
  );

  forgotRequestForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  forgotConfirmForm = this.fb.group(
    {
      code: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    },
    { validators: passwordsMatchValidator },
  );

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly requiresNewPassword = signal(false);

  /** Which sub-view of the login card is visible. */
  readonly view = signal<LoginView>('login');
  /** Email whose reset was requested — reused to confirm the code in step 2. */
  readonly resetEmail = signal('');
  /** Success notice after the new password is set, before returning to login. */
  readonly resetDone = signal(false);

  async onSubmit(): Promise<void>  {
    if (this.form.invalid) return;
    this.loading.set(true);
    this.error.set(null);

    try {
      const { email, password } = this.form.value;
      const result = await this.authService.signIn(email!, password!);
      if (result.requiresNewPassword) {
        this.requiresNewPassword.set(true);
        this.view.set('newPassword');
        return;
      }
      await this.navigateAfterLogin();
    } catch (err: unknown) {
      this.error.set((err as Error).message ?? 'Login failed');
    } finally {
      this.loading.set(false);
    }
  }

  async onConfirmNewPassword(): Promise<void> {
    if (this.newPasswordForm.invalid) return;
    this.loading.set(true);
    this.error.set(null);

    try {
      const { newPassword } = this.newPasswordForm.value;
      await this.authService.confirmNewPassword(newPassword!);
      await this.navigateAfterLogin();
    } catch (err: unknown) {
      this.error.set((err as Error).message ?? 'Failed to set new password');
    } finally {
      this.loading.set(false);
    }
  }

  /** Switch to the forgot-password request view. */
  showForgotPassword(): void {
    this.error.set(null);
    this.resetDone.set(false);
    this.forgotRequestForm.reset();
    this.view.set('forgotRequest');
  }

  /** Return to the sign-in view from any sub-view. */
  backToLogin(): void {
    this.error.set(null);
    this.view.set('login');
  }

  /** Step 1: ask Cognito to email a reset code. */
  async onRequestReset(): Promise<void> {
    if (this.forgotRequestForm.invalid) return;
    this.loading.set(true);
    this.error.set(null);

    try {
      const email = this.forgotRequestForm.value.email!;
      await this.authService.requestPasswordReset(email);
      this.resetEmail.set(email);
      this.forgotConfirmForm.reset();
      this.view.set('forgotConfirm');
    } catch (err: unknown) {
      this.error.set((err as Error).message ?? 'Could not start password reset');
    } finally {
      this.loading.set(false);
    }
  }

  /** Step 2: confirm the emailed code and set the new password. */
  async onConfirmReset(): Promise<void> {
    if (this.forgotConfirmForm.invalid) return;
    this.loading.set(true);
    this.error.set(null);

    try {
      const { code, newPassword } = this.forgotConfirmForm.value;
      await this.authService.confirmPasswordReset(
        this.resetEmail(),
        code!,
        newPassword!,
      );
      this.resetDone.set(true);
      this.view.set('login');
    } catch (err: unknown) {
      this.error.set((err as Error).message ?? 'Could not reset password');
    } finally {
      this.loading.set(false);
    }
  }
}
